import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Text, Numbers } from '@/components/ui/typography';
import { ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { UserTransaction } from './types';
import { fmtCurrency, fmtAmount } from '@/lib/formatters';
import { semanticTokens } from '@packages/config/theme';
import { cn } from '@/lib/utils';

interface TransactionCardProps {
  transaction: UserTransaction;
  onViewReceipt?: (receipt: UserTransaction['receipt']) => void;
  onClick?: () => void; // For navigation to detail page
}

export function TransactionCard({ transaction, onViewReceipt, onClick }: TransactionCardProps) {
  // Revolut-style: монохромные иконки типов (цвет только для критичных статусов)
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownLeft className={cn("w-5 h-5", semanticTokens.fg.default)} aria-hidden="true" />;
      case 'withdraw':
        return <ArrowUpRight className={cn("w-5 h-5", semanticTokens.fg.default)} aria-hidden="true" />;
      case 'liquidation':
        return <SFIcon icon={sfIcons.refresh} className={cn("w-5 h-5", semanticTokens.fg.default)} aria-hidden="true" />;
      case 'transfer':
        return <SFIcon icon={sfIcons.refresh} className={cn("w-5 h-5", semanticTokens.fg.default)} aria-hidden="true" />;
      case 'return':
        return <ArrowDownLeft className={cn("w-5 h-5", semanticTokens.fg.default)} aria-hidden="true" />;
      default:
        return <div className="w-5 h-5" aria-hidden="true" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <SFIcon icon={sfIcons.success} className={cn("w-4 h-4", semanticTokens.positive.accent)} aria-hidden="true" />;
      case 'processing':
        return <SFIcon icon={sfIcons.pending} className={cn("w-4 h-4", semanticTokens.info.accent)} aria-hidden="true" />;
      case 'pending':
        return <SFIcon icon={sfIcons.pending} className={cn("w-4 h-4", semanticTokens.warning.accent)} aria-hidden="true" />;
      case 'failed':
        return <SFIcon icon={sfIcons.error} className={cn("w-4 h-4", semanticTokens.danger.accent)} aria-hidden="true" />;
      case 'cancelled':
        return <SFIcon icon={sfIcons.warning} className={cn("w-4 h-4", semanticTokens.fg.muted)} aria-hidden="true" />;
      default:
        return null;
    }
  };

  // Revolut-style: цвет только для критичных статусов (completed, failed)
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return cn(semanticTokens.positive.bg, semanticTokens.positive.fg);
      case 'failed':
        return cn(semanticTokens.danger.bg, semanticTokens.danger.fg);
      // Остальные статусы - монохромные
      case 'processing':
      case 'pending':
      case 'cancelled':
      default:
        return cn(semanticTokens.bg.muted, semanticTokens.fg.muted);
    }
  };

  const formatAmount = (amount: { currency: string; value: string }) => {
    if (!amount) return { value: '0.00', currency: '', isUSD: false };
    const val = parseFloat(amount.value);
    
    // Bridge API precision rule: whole cents only (2 decimal places)
    // Truncate to 2 decimal places, don't round
    // Reference: https://apidocs.bridge.xyz/platform/additional-information/precision
    const truncated = Math.floor(val * 100) / 100;
    
    const currencyUpper = amount.currency.toUpperCase();
    
    // For USD and stablecoins (USDC, USDT, etc.), Bridge processes only whole cents
    const isStablecoin = ['USD', 'USDC', 'USDT', 'PYUSD', 'USDB', 'EURC', 'DAI', 'USDP'].includes(currencyUpper);
    
    if (currencyUpper === 'USD' || isStablecoin) {
      return { 
        value: fmtCurrency(truncated, 'USD'),
        currency: '',
        isUSD: true
      };
    }
    
    // For other currencies, still apply Bridge precision (2 decimal places max)
    return {
      value: fmtAmount(truncated),
      currency: currencyUpper,
      isUSD: false
    };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getExplorerUrl = (receipt: UserTransaction['receipt']) => {
    if (!receipt?.hash) return null;
    
    const explorers: Record<string, string> = {
      ethereum: `https://etherscan.io/tx/${receipt.hash}`,
      base: `https://basescan.org/tx/${receipt.hash}`,
      polygon: `https://polygonscan.com/tx/${receipt.hash}`,
      solana: `https://solscan.io/tx/${receipt.hash}`
    };
    
    return explorers[receipt.chain as string] || explorers[receipt.chain?.toLowerCase()];
  };

  const explorerUrl = getExplorerUrl(transaction.receipt);

  return (
    <Card 
      role="article"
      aria-label={`${transaction.type} transaction ${transaction.id}`}
      className={cn(
        semanticTokens.bg.surface,
        onClick && "cursor-pointer hover:shadow-md", // Make clickable if onClick provided
        semanticTokens.border.subtle, // Revolut-style: очень тонкий border
        "shadow-sm", // Revolut-style: очень мягкая тень
        semanticTokens.radius.lg,
        semanticTokens.motion.standard,
        "hover:shadow-md",
        semanticTokens.focus.ring,
        "focus-visible:outline-none"
      )}
      onClick={onClick}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardContent className={cn(
        "flex flex-col gap-6", // Revolut-style: больше spacing
        semanticTokens.spacing.lg,
        "p-6 lg:p-8" // Revolut-style: больше padding для белого пространства
      )}>
        {/* Header: Type & Status */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Type Icon */}
            <div className={cn(
              "flex-shrink-0",
              "w-10 h-10 lg:w-12 lg:h-12",
              semanticTokens.bg.muted,
              semanticTokens.radius.full,
              "flex items-center justify-center"
            )}>
              {getTypeIcon(transaction.type)}
            </div>

            {/* Type & Amount */}
            <div className="flex-1 min-w-0">
              <Text variant="lg" className="capitalize">
                {transaction.type}
              </Text>
              <div className="mt-1 flex items-center gap-2">
                {(() => {
                  const formatted = formatAmount(transaction.amount);
                  return (
                    <>
                      {!formatted.isUSD && (
                        <CurrencyIcon symbol={formatted.currency} size={24} className="shrink-0" />
                      )}
                      <Numbers variant="lg">
                        {formatted.value}
                        {formatted.currency && ` ${formatted.currency}`}
                      </Numbers>
                    </>
                  );
                })()}
              </div>
              {transaction.usd_value && transaction.amount?.currency !== 'USD' && (
                <Text variant="caption" color="muted" className="mt-1">
                  ≈ {fmtCurrency(parseFloat(transaction.usd_value), 'USD')}
                </Text>
              )}
            </div>
          </div>

          {/* Status Badge */}
          <Badge 
            variant="secondary" 
            className={cn(
              "flex items-center gap-1.5 flex-shrink-0",
              getStatusColor(transaction.status),
              semanticTokens.typography.caption
            )}
          >
            {getStatusIcon(transaction.status)}
            <span className="capitalize">{transaction.status}</span>
          </Badge>
        </div>

        {/* Details: Source/Destination */}
        {(transaction.source || transaction.destination) && (
          <div className={cn(
            "flex flex-col gap-2",
            semanticTokens.spacing.sm.replace('p-', 'gap-'),
            "pt-2 border-t",
            semanticTokens.border.subtle
          )}>
            {transaction.source && typeof transaction.source === 'object' && (
              <div className="flex items-start gap-2">
                <Text variant="caption" color="muted" as="span" className="flex-shrink-0 w-12">
                  From:
                </Text>
                <Text variant="md" as="span" className="flex-1">
                  {transaction.source.details || transaction.source.type || 'Unknown'}
                </Text>
              </div>
            )}
            {transaction.destination && typeof transaction.destination === 'object' && (
              <div className="flex items-start gap-2">
                <Text variant="caption" color="muted" as="span" className="flex-shrink-0 w-12">
                  To:
                </Text>
                <Text variant="md" as="span" className="flex-1">
                  {transaction.destination.details || transaction.destination.type || 'Unknown'}
                </Text>
              </div>
            )}
          </div>
        )}

        {/* Footer: Date, Fees, Receipt */}
        <div className={cn(
          "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3",
          "pt-2 border-t",
          semanticTokens.border.subtle
        )}>
          {/* Date */}
          <div className="flex items-center gap-2">
            <SFIcon icon={sfIcons.pending} className={cn("w-4 h-4", semanticTokens.fg.muted)} aria-hidden="true" />
            <div className="flex flex-col">
              <Text variant="caption" color="muted">
                {formatDate(transaction.created_at)}
              </Text>
              <Text variant="caption-sm" color="subtle">
                {new Date(transaction.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </div>
          </div>

          {/* Fees & Receipt */}
          <div className="flex items-center gap-3">
            {/* Fees */}
            {transaction.fees && parseFloat(transaction.fees.total_fee) > 0 && (
              <div className="flex flex-col items-end">
                <Text variant="caption" color="muted">
                  Fee
                </Text>
                <Text variant="md">
                  ${transaction.fees.total_fee}
                </Text>
              </div>
            )}

            {/* Receipt Link */}
            {explorerUrl && (
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "min-h-[44px] min-w-[44px]",
                  semanticTokens.motion.standard,
                  semanticTokens.focus.ring,
                  "focus-visible:outline-none"
                )}
                asChild
                aria-label="View transaction on blockchain explorer"
              >
                <a href={explorerUrl} target="_blank" rel="noopener noreferrer">
                  <SFIcon icon={sfIcons.externalLink} className="w-4 h-4" aria-hidden="true" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

